/** Consent-gated Google tags for the Vite/React landing.
 * Script elements rendered as JSX after hydration are not reliably executed.
 * Insert executable scripts only after the visitor has granted analytics consent.
 */
export type AnalyticsConsent = "granted" | "denied" | "unknown";

export type GoogleTagOptions = {
  consent: AnalyticsConsent;
  nonce?: string;
  gaId?: string;
  gtmId?: string;
};

const withNonce = (script: HTMLScriptElement, nonce?: string) => {
  if (nonce) script.nonce = nonce;
};

export function installGoogleTags(
  doc: Document,
  { consent, nonce, gaId, gtmId }: GoogleTagOptions,
): boolean {
  if (consent !== "granted") return false;

  const validatedGtm = /^GTM-[A-Z0-9]+$/.test(gtmId ?? "") ? gtmId : undefined;
  const validatedGa = /^G-[A-Z0-9]+$/.test(gaId ?? "") ? gaId : undefined;

  if (validatedGtm) {
    if (doc.getElementById("sendbot-gtm")) return false;
    const tag = doc.createElement("script");
    tag.id = "sendbot-gtm";
    withNonce(tag, nonce);
    tag.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${validatedGtm}');`;
    doc.head.appendChild(tag);
    return true;
  }

  if (!validatedGa || doc.getElementById("sendbot-ga4")) return false;

  const loader = doc.createElement("script");
  loader.id = "sendbot-ga4-loader";
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${validatedGa}`;
  withNonce(loader, nonce);

  const config = doc.createElement("script");
  config.id = "sendbot-ga4";
  withNonce(config, nonce);
  config.text = `window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};window.gtag('js',new Date());window.gtag('config','${validatedGa}',{send_page_view:true});`;

  doc.head.append(loader, config);
  return true;
}
